// src/components/Shared/SecurityLock.js
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { IoFingerPrintOutline, IoShieldCheckmarkOutline, IoLockClosedOutline } from 'react-icons/io5';
import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import './SecurityLock.css';

const INACTIVITY_TIMEOUT = 5 * 60 * 1000; // 5 minutes in milliseconds
const TEST_TIMEOUT = 30 * 1000; // 30 seconds for development testing (can be toggled)

export const SecurityLock = () => {
  const { currentUser, userData } = useAuth();
  const [isLocked, setIsLocked] = useState(false);
  const [pin, setPin] = useState('');
  const [isSettingPin, setIsSettingPin] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [faceIdScanning, setFaceIdScanning] = useState(false);
  const [unlockSuccess, setUnlockSuccess] = useState(false);
  const [error, setError] = useState('');
  const [usePinKeyboard, setUsePinKeyboard] = useState(false);
  const lastActiveTime = useRef(Date.now());

  // Check if PIN exists in localStorage
  const storedPin = localStorage.getItem(`kondo_pin_${currentUser?.uid}`);


  useEffect(() => {
    if (!currentUser || currentUser.isSimulated || userData?.isSimulated) {
      setIsLocked(false);
      return;
    }

    // 1. Session-based Lock Check (When tab/window is opened)
    const isSessionUnlocked = sessionStorage.getItem(`kondo_session_unlocked_${currentUser.uid}`);
    if (!isSessionUnlocked) {
      setIsLocked(true);
      if (!storedPin) {
        setIsSettingPin(true);
      }
    }

    // 2. Background Idle Detection (5 minutes)
    const checkIdle = () => {
      const timeSinceLastActive = Date.now() - lastActiveTime.current;
      if (timeSinceLastActive > INACTIVITY_TIMEOUT && !isLocked) {
        setIsLocked(true);
        sessionStorage.removeItem(`kondo_session_unlocked_${currentUser.uid}`);
        if (!storedPin) {
          setIsSettingPin(true);
        }
      }
    };

    // Update active timestamp on user interaction
    const updateActivity = () => {
      lastActiveTime.current = Date.now();
      localStorage.setItem(`kondo_last_active_${currentUser.uid}`, Date.now().toString());
    };

    // Listeners for activity
    window.addEventListener('mousemove', updateActivity);
    window.addEventListener('keydown', updateActivity);
    window.addEventListener('scroll', updateActivity);
    window.addEventListener('touchstart', updateActivity);

    const interval = setInterval(checkIdle, 10000); // Check idle status every 10 seconds

    return () => {
      window.removeEventListener('mousemove', updateActivity);
      window.removeEventListener('keydown', updateActivity);
      window.removeEventListener('scroll', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
      clearInterval(interval);
    };
  }, [currentUser, isLocked, storedPin, userData]);

  if (!currentUser || !isLocked || currentUser.isSimulated || userData?.isSimulated) return null;

  const handleNumberClick = (num) => {
    setError('');
    if (isSettingPin) {
      if (newPin.length < 4) {
        setNewPin(prev => prev + num);
      } else if (confirmPin.length < 4) {
        setConfirmPin(prev => prev + num);
      }
    } else {
      if (pin.length < 4) {
        const nextPin = pin + num;
        setPin(nextPin);
        
        // Auto submit when 4 digits reached
        if (nextPin.length === 4) {
          if (nextPin === storedPin) {
            triggerUnlockSuccess();
          } else {
            setTimeout(() => {
              setError('PIN incorrecto. Intenta de nuevo.');
              setPin('');
            }, 300);
          }
        }
      }
    }
  };

  const handleBackspace = () => {
    setError('');
    if (isSettingPin) {
      if (confirmPin.length > 0) {
        setConfirmPin(prev => prev.slice(0, -1));
      } else {
        setNewPin(prev => prev.slice(0, -1));
      }
    } else {
      setPin(prev => prev.slice(0, -1));
    }
  };

  const triggerUnlockSuccess = () => {
    setUnlockSuccess(true);
    setTimeout(() => {
      setIsLocked(false);
      setUnlockSuccess(false);
      setPin('');
      setUsePinKeyboard(false);
      sessionStorage.setItem(`kondo_session_unlocked_${currentUser.uid}`, 'true');
    }, 1000);
  };

  const handleSavePin = (e) => {
    e.preventDefault();
    if (newPin.length !== 4 || confirmPin.length !== 4) {
      setError('El PIN debe ser de 4 dígitos.');
      return;
    }
    if (newPin !== confirmPin) {
      setError('Los PINs no coinciden. Intenta de nuevo.');
      setNewPin('');
      setConfirmPin('');
      return;
    }

    localStorage.setItem(`kondo_pin_${currentUser.uid}`, newPin);
    setIsSettingPin(false);
    triggerUnlockSuccess();
  };

  const triggerBiometricScan = async () => {
    if (isSettingPin) return;
    setError('');
    
    // Fallback for Web/PWA simulation
    if (Capacitor.getPlatform() === 'web') {
      setFaceIdScanning(true);
      setTimeout(() => {
        setFaceIdScanning(false);
        triggerUnlockSuccess();
      }, 1800);
      return;
    }

    // REAL NATIVE DEVICE BIOMETRICS
    try {
      const result = await NativeBiometric.isAvailable();
      if (!result.isAvailable) {
        setError('Acceso biométrico no disponible en este dispositivo.');
        setUsePinKeyboard(true); // Fallback to PIN keyboard automatically
        return;
      }

      setFaceIdScanning(true);
      await NativeBiometric.verifyIdentity({
        reason: 'Usa tu huella dactilar o Face ID para iniciar sesión',
        title: 'Verificación Biométrica',
        subtitle: 'Acceso seguro a Kondify',
        description: 'Coloca tu dedo en el sensor o usa reconocimiento facial.',
        allowDeviceCredential: true
      });

      setFaceIdScanning(false);
      triggerUnlockSuccess();
    } catch (err) {
      console.error('Biometric authentication failed:', err);
      setFaceIdScanning(false);
      setError('Verificación biométrica rechazada o cancelada.');
    }
  };

  return (
    <div className="security-lock-overlay">
      <div className="security-lock-card">
        <div className="security-lock-header">
          <div className="security-lock-shield-icon">
            {unlockSuccess ? (
              <IoShieldCheckmarkOutline className="unlock-pulse-icon" />
            ) : (
              <IoLockClosedOutline />
            )}
          </div>
          <h2>
            {isSettingPin 
              ? (newPin.length < 4 ? 'Crea tu PIN de Seguridad' : 'Confirma tu PIN')
              : `Bienvenido de nuevo, ${userData?.nombre?.split(' ')[0] || 'Residente'}`
            }
          </h2>
          <p className="security-lock-subtitle">
            {isSettingPin
              ? 'Ingresa un PIN de 4 dígitos para proteger tu cuenta'
              : 'Introduce tu PIN de acceso o usa reconocimiento biométrico'
            }
          </p>
        </div>

        {error && <div className="security-lock-error">{error}</div>}

        {/* Default Biometric Scan Screen or PIN Keyboard Screen */}
        {!isSettingPin && !usePinKeyboard ? (
          <div className="biometric-default-view">
            <div className="biometric-trigger-container" onClick={triggerBiometricScan}>
              <div className="biometric-pulse" style={{ display: faceIdScanning ? 'block' : 'none' }} />
              <IoFingerPrintOutline className={`biometric-main-icon ${faceIdScanning ? 'scanning' : ''}`} />
              <span className="biometric-main-text">
                {faceIdScanning ? 'Escaneando datos biométricos...' : 'Presiona para escanear huella'}
              </span>
            </div>
            
            <div className="biometric-switch-to-pin">
              <button 
                type="button" 
                className="switch-pin-btn" 
                onClick={() => setUsePinKeyboard(true)}
              >
                PIN
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* PIN Indicators */}
            <div className="pin-indicators">
              {Array(4).fill(0).map((_, i) => {
                let active = false;
                if (isSettingPin) {
                  active = newPin.length < 4 ? i < newPin.length : i < confirmPin.length;
                } else {
                  active = i < pin.length;
                }
                return <div key={i} className={`pin-dot ${active ? 'active' : ''} ${unlockSuccess ? 'success' : ''}`} />;
              })}
            </div>

            {/* PIN Keyboard */}
            <div className="pin-keyboard">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                <button 
                  key={num} 
                  type="button" 
                  className="pin-key"
                  onClick={() => handleNumberClick(num.toString())}
                >
                  {num}
                </button>
              ))}
              
              {/* Biometric unlock option (switches back to fingerprint scanner) */}
              <button 
                type="button" 
                className={`pin-key helper-key ${isSettingPin ? 'disabled' : ''}`}
                onClick={() => {
                  if (!isSettingPin) {
                    setUsePinKeyboard(false);
                  }
                }}
                disabled={isSettingPin}
                title="Reconocimiento Facial / Huella"
              >
                <IoFingerPrintOutline size={26} />
              </button>

              <button 
                key={0} 
                type="button" 
                className="pin-key"
                onClick={() => handleNumberClick('0')}
              >
                0
              </button>

              <button 
                type="button" 
                className="pin-key helper-key"
                onClick={handleBackspace}
              >
                ⌫
              </button>
            </div>
          </>
        )}

        {isSettingPin && newPin.length === 4 && confirmPin.length === 4 && (
          <button 
            type="button" 
            className="security-lock-btn"
            onClick={handleSavePin}
          >
            Confirmar y Activar
          </button>
        )}
      </div>
    </div>
  );
};

export default SecurityLock;
